import React from 'react'

import { HeaderBackButton } from '@react-navigation/elements'
import { createStackNavigator } from '@react-navigation/stack'
import client from '@sart/client'
import {
  View,
  StyleSheet,
  Image
} from 'react-native'
import {
  Text
} from '@sart/components'
import {
  adaptive,
  colors,
  backgroundColors,
  theme,
  typography
} from '@sart/styles'
import { useContent } from '@sart/content'

import { stackNavigationProps } from '../nagivationProps'
import Confirmation from '../Confirmation'

import Menu from './Menu'
import Preferences from './Preferences'
import Legal from './Legal'
import LegalItem from './LegalItem'
import TermsAndConditions from './TermsAndConditions'
import TermsAndConditionsItem from './TermsAndConditionsItem'
import FAQs from './FAQs'
import FAQsItem from './FAQsItem'
import HelpScreen from './HelpScreen'

import Profile from './Profile'
import ServiceSettings from './ServiceSettings'
import ProfileSummary from './ProfileSummary'
import ProfileIdentification from './ProfileIdentification'
import ProfileAddress from './ProfileAddress'
import SwitchNumber from './SwitchNumber'
import SwitchPortOut from './SwitchPortOut'
// import Confirmation from '../Confirmation'
import ProfilePayment from './ProfilePayment'
import ProfileHostedPayment from './ProfileHostedPayment'
import ProfilePaymentProcessing from './ProfilePaymentProcessing'
import ProfilePaymentConfirmation from './ProfilePaymentConfirmation'
import PaymentHistory from './PaymentHistory'
import ManageSubscription from './ManageSubscription'
import ChangePlan from './ChangePlan'
import ChangePlanReview from './ChangePlanReview'
import CancelService from './CancelService'
import CancelServiceConfirmation from './CancelServiceConfirmation'
import About from './About'

import legalList from './legal.json'
import helpList from './help.json'
import { CompanyIcon } from '@sart/icons'
const Stack = createStackNavigator()

function MenuStackNavigator({ navigation, route }) {

  const content = useContent()

  // Listen for tab presses from the parent tab navigator and reset this stack to its root
  React.useEffect(() => {
    const parent = navigation.getParent()
    if (!parent || typeof parent.addListener !== 'function') return

    const unsubscribe = parent.addListener('tabPress', e => {
      // When the Menu tab is pressed, make sure this stack returns to its root 'Menu' screen
      // We do not prevent default so the tab still focuses as expected.
      try {
        navigation.popToTop()
        navigation.navigate('Menu')
      } catch (err) {
        // swallow any errors — this is best-effort
        // console.log('error resetting menu stack on tabPress', err)
      }
    })

    return unsubscribe
  }, [navigation])

  // When this stack is focused, check for a pending one-time request from Dashboard to open a child screen.
  // If present, navigate to it and clear the flag.
  React.useEffect(() => {
    const unsubscribeFocus = navigation.addListener('focus', () => {
      try {
        const pending = client?.cache?.pendingMenuScreen
        if (pending) {
          // Navigate to requested nested screen (e.g., 'ChangePlan')
          navigation.navigate(pending)
          // Clear the one-time request
          client.setCache({ pendingMenuScreen: undefined })
        }
      } catch (err) {
        // swallow any errors — best-effort
      }
    })

    return unsubscribeFocus
  }, [navigation])

  return (
    <Stack.Navigator
      initialRouteName='Menu'
      screenOptions={{
        headerTitleAlign: 'center',
        ...stackNavigationProps.screenOptions
      }}
    >
      <Stack.Screen
        name="Menu"
        component={Menu}
        options={{
          title: "",
          headerLeft: () => (<View style={{ paddingLeft: 15 }}>
            <Image
              source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
              style={styles.logoImage}
            />
          </View>),
          headerTopInsetEnabled: true
        }}
      />
      <Stack.Screen
        name="Preferences"
        component={Preferences}
        options={{
          title: content.menu.preferences,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.menu.preferences}</Text>,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="Legal"
        component={Legal}
        options={({ navigation }) => ({
          title: content.legal.header,
          headerTitleStyle: theme.navigationHeader,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.legal.header}</Text>,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="LegalItem"
        component={LegalItem}
        options={{
          title: content.legal.header,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.legal.header}</Text>,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="TermsAndConditions"
        component={TermsAndConditions}
        options={{
          title: content.navigation.termsAndConditions,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.termsAndConditions}</Text>,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="TermsAndConditionsItem"
        component={TermsAndConditionsItem}
        options={{
          title: content.navigation.termsAndConditions,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.termsAndConditions}</Text>,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="FAQs"
        component={FAQs}
        options={({ navigation }) => ({
          title: content.navigation.faq,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.faq}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.popToTop()
                navigation.navigate('Menu')
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="FAQsItem"
        component={FAQsItem}
        options={{
          title: content.navigation.faq,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.faq}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="HelpScreen"
        component={HelpScreen}
        options={({ navigation }) => ({
          title: content.menu.help,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.menu.help}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Profile"
        component={Profile}
        options={({ navigation }) => ({
          title: content.navigation.profile,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.profile}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Account"
        component={ProfileSummary}
        options={({ navigation }) => ({
          title: content.navigation.profile,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.profile}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.bill}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="PaymentHistory"
        component={PaymentHistory}
        options={({ navigation }) => ({
          title: content.navigation.paymentHistory,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.paymentHistory}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Identification"
        component={ProfileIdentification}
        options={({ navigation }) => ({
          title: content.navigation.identification,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.identification}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.bill}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Address"
        component={ProfileAddress}
        options={({ navigation }) => ({
          title: content.navigation.identification,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.identification}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.bill}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Payment"
        component={ProfilePayment}
        options={({ navigation }) => ({
          title: content.profile_summary.payment,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.profile_summary.payment}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.bill}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="ManageSubscription"
        component={ManageSubscription}
        options={({ navigation }) => ({
          title: content.navigation.manageSubscription,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.manageSubscription}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Settings"
        component={ServiceSettings}
        options={({ navigation }) => ({
          title: content.service_settings.title,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.service_settings.title}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.bill}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Switch"
        component={SwitchNumber}
        options={({ navigation }) => ({
          title: content.navigation.switch,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.switch}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.popToTop()
                navigation.navigate('ManageSubscription')
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="SwitchPortOut"
        component={SwitchPortOut}
        options={({ navigation }) => ({
          title: content.navigation.portOut,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.portOut}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.popToTop()
                navigation.navigate('ManageSubscription')
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="ChangePlan"
        component={ChangePlan}
        options={({ navigation }) => ({
          title: content.profile.changePlanLabel,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.profile.changePlanLabel}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.navigate('ManageSubscription')
                // navigation.popToTop()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="ChangePlanReview"
        component={ChangePlanReview}
        options={({ navigation }) => ({
          title: content.profile.changePlanLabel,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.profile.changePlanLabel}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="CancelService"
        component={CancelService}
        options={({ navigation }) => ({
          title: content.profile.cancelServiceLabel,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.profile.cancelServiceLabel}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="CancelServiceConfirmation"
        component={CancelServiceConfirmation}
        options={({ navigation }) => ({
          title: content.cancel.confirm,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.cancel.confirm}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
      <Stack.Screen
        name="Confirmation"
        component={Confirmation}
        options={({ navigation }) => ({
          title: '',
          headerRight: () => (<View />),
          headerLeft: props => (
            <HeaderBackButton
              {...props}
              label={content.navigation.dashboard}
              onPress={() => {
                navigation.popToTop()
                navigation.navigate('Dashboard')
              }}
              tintColor={colors.white}
            />
          ),
        })}
      />
      <Stack.Screen
        name="ProfileHostedPayment"
        component={ProfileHostedPayment}
        options={{
          title: content.profile_summary.payment,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.profile_summary.payment}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="ProfilePaymentProcessing"
        component={ProfilePaymentProcessing}
        options={{
          title: content.profile_summary.payment,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.profile_summary.payment}</Text>,
          headerTitleStyle: theme.navigationHeader,
          headerRight: () => (<View />)
        }}
      />
      <Stack.Screen
        name="ProfilePaymentConfirmation"
        component={ProfilePaymentConfirmation}
        options={{
          title: '',
          headerRight: () => (<View />),
          headerLeft: null,
        }}
      />
      <Stack.Screen
        name="About"
        component={About}
        options={({ navigation }) => ({
          title: content.navigation.about,
          headerTitleStyle: theme.navigationHeader,
          headerCenter: () => <Text style={theme.navigationHeader}>{content.navigation.about}</Text>,
          headerRight: () => (<View />),
          headerLeft: () => (
            <HeaderBackButton
              backTitleVisible={true}
              title={content.navigation.menu}
              onPress={() => {
                navigation.goBack()
              }}
              tintColor={colors.brightOrange}
            />
          )
        })}
      />
    </Stack.Navigator>
  )
}

export default MenuStackNavigator

const styles = StyleSheet.create({
  container: {
    paddingTop: 24,
    paddingBottom: 24,
    ...adaptive.wrapperPadding,
  },
  description: {
    textAlign: 'center'
  },
  itemStyle: {
    ...typography.lightbold
  },
  itemViewStyle: {
    flex: 1
  },
  itemHeaderStyle: {
    ...theme.navigationItemHeader,
  },
  logoImage: {
    height: 45,
    width: 45,
    position: 'relative'
  }
})
